import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLang } from '@/contexts/LangContext';
import api from '@/lib/api';

export default function VerifyEmailPage() {
  const { t } = useLang();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const token = params.get('token');
  const redirectTo = params.get('redirect');

  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('missing');
      return;
    }

    setStatus('loading');
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setStatus('success'))
      .catch((err) => {
        setErrorMessage(err.response?.data?.error || '');
        setStatus('error');
      });
  }, [token]);

  const renderIcon = () => {
    if (status === 'loading') return <Loader2 className="h-6 w-6 animate-spin" />;
    if (status === 'success') return <CheckCircle2 className="h-6 w-6" />;
    return <XCircle className="h-6 w-6" />;
  };

  const renderTitle = () => {
    if (status === 'success') return t('verify.success_title');
    if (status === 'missing') return t('verify.missing_title');
    if (status === 'error') return t('verify.failed_title');
    return t('common.loading');
  };

  const renderDesc = () => {
    if (status === 'success') return t('verify.success_desc');
    if (status === 'missing') return t('verify.missing_desc');
    if (status === 'error') return errorMessage || t('verify.failed_desc');
    return t('common.loading');
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <Card className="rounded-2xl shadow-sm">
          <CardContent className="p-8 sm:p-10">
            <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-6 ${status === 'success' ? 'bg-green-500/10 text-green-600' : status === 'loading' ? 'bg-muted text-muted-foreground' : 'bg-red-500/10 text-red-600'}`}>
              {renderIcon()}
            </div>
            <h1 className="text-2xl font-semibold mb-2">{renderTitle()}</h1>
            <p className="text-sm text-muted-foreground">{renderDesc()}</p>

            <div className="mt-6 flex flex-col gap-3">
              <Link to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}>
                <Button className="w-full rounded-xl h-11">{t('verify.back_login')}</Button>
              </Link>
              {(status === 'error' || status === 'missing') && (
                <Link to="/verify-email/sent">
                  <Button variant="outline" className="w-full rounded-xl h-11">
                    {t('verify.resend')}
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
